import { useState } from 'react'

import { Box, Card, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Select } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

import { amountWithCurrency } from 'modules/finance/lib/budgets'

import type { BudgetAllocationTypes, BudgetExceededAmounts, BudgetExceededOptions } from './types'

const allocationOptions = () => [
  {
    value: 'none',
    label: i18n.t('budgets.forms.new_budget.spenders.allocations.options.none'),
  },
  {
    value: 'equal',
    label: i18n.t('budgets.forms.new_budget.spenders.allocations.options.equal'),
  },
]

interface BudgetExceededOptionSelectorProps {
  size?: 'small' | 'medium'
  currency: string
  amounts: BudgetExceededAmounts
  onSelect: (option: BudgetExceededOptions) => void
}

export const BudgetExceededOptionSelector = ({
  size = 'medium',
  currency,
  amounts,
  onSelect,
}: BudgetExceededOptionSelectorProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const [selected, setSelected] = useState<BudgetExceededOptions>()

  const handleSelect = (option: BudgetExceededOptions) => {
    setSelected(option)
    onSelect(option)
  }

  return (
    <Box flexDirection={size === 'small' ? 'column' : 'row'} gap="s16">
      <Box width="full">
        <Card
          onClick={() => {
            handleSelect('increaseBudget')
          }}
        >
          <Card.Title
            size={size}
            action={
              <RadioButton
                size="medium"
                checked={selected == 'increaseBudget'}
                value="increaseBudget"
                onChange={() => {}}
              />
            }
          >
            {i18n.t('budgets.budget_exceeded_option_selector.card_1.title')}
          </Card.Title>
          <Card.Content>
            <Text.Normal>
              {i18n.tView('budgets.budget_exceeded_option_selector.card_1.description', {
                previousLimit: (
                  <Text size="300" weight="semibold">
                    {amountWithCurrency(toCents(amounts.budgetAmount), currency, locale)}
                  </Text>
                ),

                newLimit: (
                  <Text size="300" weight="semibold">
                    {amountWithCurrency(toCents(amounts.totalAllocatedAmount), currency, locale)}
                  </Text>
                ),
              })}
            </Text.Normal>
          </Card.Content>
        </Card>
      </Box>
      {amounts.maxAllocationAmount > 0 && (
        <Box width="full">
          <Card
            onClick={() => {
              handleSelect('changeAllocation')
            }}
          >
            <Card.Title
              size={size}
              action={
                <RadioButton
                  size="medium"
                  checked={selected == 'changeAllocation'}
                  onChange={() => {}}
                  value="changeAllocation"
                />
              }
            >
              {i18n.t('budgets.budget_exceeded_option_selector.card_2.title')}
            </Card.Title>
            <Card.Content>
              <Text.Normal>
                {i18n.tView('budgets.budget_exceeded_option_selector.card_2.description', {
                  newAmount: (
                    <Text size="300" weight="semibold">
                      {amountWithCurrency(toCents(amounts.maxAllocationAmount), currency, locale)}
                    </Text>
                  ),
                })}
              </Text.Normal>
            </Card.Content>
          </Card>
        </Box>
      )}
    </Box>
  )
}

interface SpendersFieldValue {
  allocationType: string
  allocatedAmountCents?: number
  spenderSelection:
    | { type: 'manual'; ids: string[] }
    | {
        type: 'all'
        filters?: Record<string, unknown>
        excludedIds: string[]
        includeInactive?: boolean
      }
}

interface SpendersFieldRendererProps {
  value: SpendersFieldValue
  onChange: (value: SpendersFieldValue) => void
  currency: string
  budgetExceededAmounts?: BudgetExceededAmounts
  onBudgetExceededOption: (option: BudgetExceededOptions) => void
}

const SpendersFieldRenderer = ({
  value,
  onChange,
  currency,
  budgetExceededAmounts,
  onBudgetExceededOption,
}: SpendersFieldRendererProps) => {
  const allocationSelectOptions = allocationOptions().map((option) => ({
    value: option.value,
    label: option.label,
  }))

  return (
    <Box gap="s40" paddingRight="s40">
      <Box gap="s16" width="fit-content">
        <Box flexDirection="row" gap="s16">
          <Box style={{ minWidth: '200px' }}>
            <F0Select
              required
              label={i18n.t('budgets.forms.new_budget.spenders.allocations.selector')}
              options={allocationSelectOptions}
              value={value.allocationType}
              onChange={(v: string) =>
                onChange({
                  ...value,
                  // eslint-disable-next-line no-type-assertion/no-type-assertion
                  allocationType: v as BudgetAllocationTypes,
                  allocatedAmountCents: v === 'none' ? undefined : value.allocatedAmountCents,
                })
              }
              size="md"
            />
          </Box>
          {value.allocationType === 'equal' && (
            <Box style={{ minWidth: '200px' }}>
              <NumberInput
                required
                label={i18n.t('budgets.forms.new_budget.spenders.allocations.amount')}
                locale={i18n.locale}
                units={currency}
                value={value.allocatedAmountCents ?? null}
                onChange={(v: number | null) =>
                  onChange({
                    ...value,
                    allocatedAmountCents: v ?? undefined,
                  })
                }
                maxDecimals={2}
                size="md"
              />
            </Box>
          )}
        </Box>
        {value.allocationType === 'equal' && !budgetExceededAmounts && (
          <F0Alert
            variant="info"
            title=""
            description={i18n.t('budgets.forms.new_budget.spenders.allocations.hint')}
          />
        )}
      </Box>
      {budgetExceededAmounts && (
        <Box gap="s16">
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('budgets.budget_exceeded_option_selector.prompt')}
          </Text.Normal>
          <BudgetExceededOptionSelector
            currency={currency}
            amounts={budgetExceededAmounts}
            onSelect={onBudgetExceededOption}
          />
        </Box>
      )}
      <Box gap="s40" maxWidth="s600">
        <Box gap="s8">
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('budgets.forms.new_budget.spenders.employees.title')}
          </Text.Normal>
          <Text.Normal>
            {i18n.t('budgets.forms.new_budget.spenders.employees.description')}
          </Text.Normal>
        </Box>
        <Box flexDirection="column">
          <EmployeeSelectorV2
            multiple
            value={
              value.spenderSelection.type === 'manual' ? value.spenderSelection.ids : undefined
            }
            onChange={(selection: EmployeeSelectorV2MultiSelectState) =>
              onChange({ ...value, spenderSelection: selection })
            }
            label={i18n.t('budgets.forms.new_budget.spenders.employees.label')}
            messages={{
              placeholder: i18n.t('budgets.forms.new_budget.spenders.employees.placeholder'),
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default SpendersFieldRenderer
